import React, { useState, useEffect, Fragment } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
 Row, Col, Card, Button, Form, Table, Badge, Spinner, Modal,
} from "react-bootstrap";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";

const blankSalary = { basic: "", da: "", hra: "", medical: "", conveyance: "" };

const blankCompliance = {
 pfApplicable: false,
 pfNumber: "",
 uan: "",
 pfRate: 12,
 esicApplicable: false,
 esicNumber: "",
 esicRate: 0.75,
 ptApplicable: false,
 ptState: "",
 tdsApplicable: false,
 tdsType: "FIXED",
 tdsAmount: "",
 tdsRate: "",
 wcApplicable: false,
 wcPolicyNumber: "",
 wcInsuranceVendor: "",
};

const PayrollSettings = () => {
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);
 const [staffList, setStaffList] = useState([]);
 const [search, setSearch] = usePersistentState("payroll_search", "");

 const [showModal, setShowModal] = useState(false);
 const [currentEmp, setCurrentEmp] = useState(null);
 const [salary, setSalary] = useState(blankSalary);
 const [compliance, setCompliance] = useState(blankCompliance);

 // auto-calculated gross
 const gross = ["basic", "da", "hra", "medical", "conveyance"]
 .reduce((sum, f) => sum + Number(salary[f] || 0), 0);

 // Load all payroll staff (no work order needed) 
 useEffect(() => { loadStaff(); }, []);

 const loadStaff = async () => {
 setLoading(true);
 try {
 const res = await api.get("/staff-salary/overview");
 setStaffList(res.data.data || []);
 } catch {
 swal("Error", "Failed to load staff", "error");
 }
 setLoading(false);
 };

 // Open edit modal 
 const openEdit = async (emp) => {
 setCurrentEmp(emp);
 setSalary(blankSalary);
 setCompliance(blankCompliance);
 setShowModal(true);

 try {
 // Load current month/year salary (staff route — no workOrderId)
 const now = new Date();
 const m = now.getMonth();
 const y = now.getFullYear();
 const [salRes, compRes] = await Promise.allSettled([
 api.get(`/staff-salary/${emp._id}/${m}/${y}`),
 api.get(`/compliance/${emp._id}/null`),
 ]);

 if (salRes.status === "fulfilled" && salRes.value.data.record) {
 const r = salRes.value.data.record;
 setSalary({
 basic: r.basic || "",
 da: r.da || "",
 hra: r.hra || "",
 medical: r.medical || "",
 conveyance: r.conveyance || "",
 });
 }

 if (compRes.status === "fulfilled" && compRes.value.data.record) {
 setCompliance({ ...blankCompliance, ...compRes.value.data.record });
 }
 } catch (err) {
 console.log("Failed loading data", err);
 }
 };

 // Save 
 const handleSave = async () => {
 if (!currentEmp) return;
 setSaving(true);
 try {
 const now = new Date();
 const m = now.getMonth();
 const y = now.getFullYear();

 // 1. Save salary
 await api.post(`/staff-salary/${currentEmp._id}/${m}/${y}`, salary);

 // 2. Save compliance (workOrderId = null → global)
 await api.post(`/compliance/${currentEmp._id}/null`, compliance);

 swal("Saved!", "Payroll settings updated", "success");
 setShowModal(false);
 loadStaff();
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to save", "error");
 }
 setSaving(false);
 };

 const filtered = staffList.filter((s) => {
 const q = search.toLowerCase();
 return !q || s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
 });

 return (
 <Fragment>
 <PageTitle activeMenu="Payroll Settings" motherMenu="HR" />

 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title className="mb-0">Payroll Settings</Card.Title>
 <Form.Control
 style={{ maxWidth: 280 }}
 placeholder="Search staff..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </Card.Header>

 <Card.Body className="p-0">
 {loading ? (
 <div className="text-center py-5"><Spinner /></div>
 ) : (
 <Table bordered hover responsive size="sm" className="mb-0">
 <thead className="bg-light">
 <tr>
 <th>#</th>
 <th>Name</th>
 <th>Contact</th>
 <th>Gross Salary</th>
 <th>Compliance</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {filtered.length === 0 ? (
 <tr>
 <td colSpan={6} className="text-center text-muted py-4">
 No payroll staff enrolled
 </td>
 </tr>
 ) : filtered.map((emp, i) => (
 <tr key={emp._id}>
 <td>{i + 1}</td>
 <td>
 <strong>{emp.name}</strong>
 <div className="text-muted small">{emp.level || "Staff"}</div>
 </td>
 <td>
 <div>{emp.email || "-"}</div>
 <div className="text-muted small">{emp.mobile || "-"}</div>
 </td>
 <td>
 {emp.gross
 ? `₹${Number(emp.gross).toLocaleString("en-IN")}`
 : <span className="text-warning">Not configured</span>}
 </td>
 <td>
 {emp.compliance?.pfApplicable && <Badge bg="success" className="me-1">PF</Badge>}
 {emp.compliance?.esicApplicable && <Badge bg="info" className="me-1">ESIC</Badge>}
 {emp.compliance?.ptApplicable && <Badge bg="warning" text="dark" className="me-1">PT</Badge>}
 {emp.compliance?.tdsApplicable && <Badge bg="primary" className="me-1">TDS</Badge>}
 {emp.compliance?.wcApplicable && <Badge bg="dark" className="me-1">WC</Badge>}
 {!emp.compliance?.pfApplicable && !emp.compliance?.esicApplicable &&
 !emp.compliance?.ptApplicable && !emp.compliance?.tdsApplicable && !emp.compliance?.wcApplicable &&
 <span className="text-muted small">None</span>}
 </td>
 <td>
 <Button size="sm" onClick={() => openEdit(emp)}>Edit</Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>

 {/* Edit Modal */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
 <Modal.Header closeButton>
 <Modal.Title>Payroll Settings — {currentEmp?.name}</Modal.Title>
 </Modal.Header>
 <Modal.Body>

 {/* Salary Structure */}
 <h6 className="mb-3">Salary Structure</h6>
 <Row className="g-2 mb-1">
 {[
 { key: "basic", label: "Basic" },
 { key: "da", label: "DA" },
 { key: "hra", label: "HRA" },
 { key: "medical", label: "Medical Allowance" },
 { key: "conveyance", label: "Conveyance Allowance" },
 ].map(({ key, label }) => (
 <Col md={4} key={key}>
 <Form.Label className="small mb-1">{label}</Form.Label>
 <Form.Control
 type="number"
 placeholder="0"
 value={salary[key] || ""}
 onChange={(e) => setSalary({ ...salary, [key]: e.target.value })}
 />
 </Col>
 ))}
 <Col md={4}>
 <Form.Label className="small mb-1">Gross (auto)</Form.Label>
 <Form.Control value={`₹${gross.toLocaleString("en-IN")}`} readOnly disabled />
 </Col>
 </Row>

 <hr />

 {/* Compliance */}
 <h6 className="mb-3">Compliance / Deductions</h6>

 {/* PF */}
 <Form.Check
 label="PF Applicable"
 checked={compliance.pfApplicable}
 onChange={(e) => setCompliance({ ...compliance, pfApplicable: e.target.checked })}
 />
 {compliance.pfApplicable && (
 <Row className="mt-2 g-2 ms-1">
 <Col md={4}>
 <Form.Label className="small mb-1">PF Number</Form.Label>
 <Form.Control
 placeholder="PF Number"
 value={compliance.pfNumber || ""}
 onChange={(e) => setCompliance({ ...compliance, pfNumber: e.target.value })}
 />
 </Col>
 <Col md={4}>
 <Form.Label className="small mb-1">UAN</Form.Label>
 <Form.Control
 placeholder="UAN"
 value={compliance.uan || ""}
 onChange={(e) => setCompliance({ ...compliance, uan: e.target.value })}
 />
 </Col>
 <Col md={4}>
 <Form.Label className="small mb-1">PF Rate (%)</Form.Label>
 <Form.Control
 type="number"
 step="0.1"
 value={compliance.pfRate ?? 12}
 onChange={(e) => setCompliance({ ...compliance, pfRate: Number(e.target.value) })}
 />
 <div className="text-muted" style={{ fontSize: 11 }}>Default 12%</div>
 </Col>
 </Row>
 )}

 {/* ESIC */}
 <Form.Check
 className="mt-3"
 label="ESIC Applicable"
 checked={compliance.esicApplicable}
 onChange={(e) => setCompliance({ ...compliance, esicApplicable: e.target.checked })}
 />
 {compliance.esicApplicable && (
 <Row className="mt-2 g-2 ms-1">
 <Col md={4}>
 <Form.Label className="small mb-1">ESIC Number</Form.Label>
 <Form.Control
 placeholder="ESIC Number"
 value={compliance.esicNumber || ""}
 onChange={(e) => setCompliance({ ...compliance, esicNumber: e.target.value })}
 />
 </Col>
 <Col md={4}>
 <Form.Label className="small mb-1">ESIC Rate (%)</Form.Label>
 <Form.Control
 type="number"
 step="0.01"
 value={compliance.esicRate ?? 0.75}
 onChange={(e) => setCompliance({ ...compliance, esicRate: Number(e.target.value) })}
 />
 <div className="text-muted" style={{ fontSize: 11 }}>Default 0.75%</div>
 </Col>
 </Row>
 )}

 {/* PT */}
 <Form.Check
 className="mt-3"
 label="PT Applicable (Professional Tax)"
 checked={compliance.ptApplicable}
 onChange={(e) => setCompliance({ ...compliance, ptApplicable: e.target.checked })}
 />
 {compliance.ptApplicable && (
 <Row className="mt-2 g-2 ms-1">
 <Col md={4}>
 <Form.Label className="small mb-1">State</Form.Label>
 <Form.Control
 placeholder="e.g. Maharashtra"
 value={compliance.ptState || ""}
 onChange={(e) => setCompliance({ ...compliance, ptState: e.target.value })}
 />
 </Col>
 <Col md={8}>
 <Form.Label className="small mb-1">PT Slabs</Form.Label>
 <div className="text-muted small p-2 border rounded bg-light">
 ≤ ₹7,500 → ₹0 &nbsp;|&nbsp; ≤ ₹10,000 → ₹175 &nbsp;|&nbsp; &gt; ₹10,000 → ₹200
 </div>
 </Col>
 </Row>
 )}

 {/* TDS */}
 <Form.Check
 className="mt-3"
 label="TDS Applicable (Tax Deducted at Source)"
 checked={compliance.tdsApplicable}
 onChange={(e) => setCompliance({ ...compliance, tdsApplicable: e.target.checked })}
 />
 {compliance.tdsApplicable && (
 <div className="mt-2 p-2 border rounded bg-light ms-1">
   <div className="d-flex gap-3 mb-2">
     <Form.Check
       type="radio"
       id="settingsTdsTypeFixed"
       name="settingsTdsType"
       label="Fixed Monthly Amount (₹)"
       checked={(compliance.tdsType || "FIXED") === "FIXED"}
       onChange={() => setCompliance({ ...compliance, tdsType: "FIXED", tdsRate: "" })}
     />
     <Form.Check
       type="radio"
       id="settingsTdsTypePercentage"
       name="settingsTdsType"
       label="Percentage of Salary (%)"
       checked={compliance.tdsType === "PERCENTAGE"}
       onChange={() => setCompliance({ ...compliance, tdsType: "PERCENTAGE", tdsAmount: "" })}
     />
   </div>
   <Row className="g-2">
     {(compliance.tdsType || "FIXED") === "FIXED" ? (
       <Col md={6}>
         <Form.Label className="small mb-1">TDS Monthly Amount (₹)</Form.Label>
         <Form.Control
           type="number"
           placeholder="e.g. 2000"
           value={compliance.tdsAmount || ""}
           onChange={(e) => setCompliance({ ...compliance, tdsAmount: e.target.value, tdsRate: "" })}
         />
         <div className="text-muted" style={{ fontSize: 11 }}>Fixed ₹ deduction per month</div>
       </Col>
     ) : (
       <Col md={6}>
         <Form.Label className="small mb-1">TDS Rate (%)</Form.Label>
         <Form.Control
           type="number"
           step="0.1"
           placeholder="e.g. 10"
           value={compliance.tdsRate || ""}
           onChange={(e) => setCompliance({ ...compliance, tdsRate: e.target.value, tdsAmount: "" })}
         />
         <div className="text-muted" style={{ fontSize: 11 }}>% deducted from earned salary</div>
       </Col>
     )}
   </Row>
 </div>
 )}

 {/* WC */}
 <Form.Check
 className="mt-3"
 label="WC Applicable (Workers Compensation)"
 checked={compliance.wcApplicable}
 onChange={(e) => setCompliance({ ...compliance, wcApplicable: e.target.checked })}
 />
 {compliance.wcApplicable && (
 <Row className="mt-2 g-2 ms-1">
 <Col md={4}>
 <Form.Label className="small mb-1">Policy Number</Form.Label>
 <Form.Control
 placeholder="Policy Number"
 value={compliance.wcPolicyNumber || ""}
 onChange={(e) => setCompliance({ ...compliance, wcPolicyNumber: e.target.value })}
 />
 </Col>
 <Col md={4}>
 <Form.Label className="small mb-1">Insurance Vendor</Form.Label>
 <Form.Control
 placeholder="Insurance Vendor"
 value={compliance.wcInsuranceVendor || ""}
 onChange={(e) => setCompliance({ ...compliance, wcInsuranceVendor: e.target.value })}
 />
 </Col>
 </Row>
 )}

 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
 <Button variant="success" disabled={saving} onClick={handleSave}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default PayrollSettings;
